// Tipos compartidos del dominio (DOMAIN §14.2 `types.ts`).
// Los valores de los enumerados son los de los CHECK de DATA_MODEL; las etiquetas en español
// viven en @atte/contracts/enums.ts.

export type Id = string; // uuid
export type ISODate = string; // 'YYYY-MM-DD'
export type Month = string; // 'YYYY-MM'
export type HHMM = string; // 'HH:MM'
export type Instant = string; // ISO 8601 con offset
export type Pesos = number; // entero
export type Pct = number; // 0..100, 2 decimales
export type Qty = number; // 3 decimales

export type Role = 'owner' | 'manager' | 'staff';
export type Vertical = 'barberia' | 'peluqueria' | 'estetica' | 'unas' | 'salud' | 'spa' | 'otro';
export type ModuleKey = 'sales' | 'stock' | 'commissions' | 'giftcards' | 'finance' | 'dashboard';
export type AppointmentStatus = 'booked' | 'confirmed' | 'completed' | 'no_show' | 'cancelled';
export type AppointmentSource = 'staff' | 'online';
export type SaleStatus = 'active' | 'voided';
export type LineKind = 'service' | 'product';
export type ProductKind = 'resale' | 'drink' | 'supply';
export type ExtraKind = 'courtesy' | 'supply';
export type StockMovementKind =
  | 'opening'
  | 'purchase'
  | 'sale'
  | 'recipe'
  | 'extra_courtesy'
  | 'extra_supply'
  | 'internal_use'
  | 'courtesy'
  | 'adjustment'
  | 'waste'
  | 'reversal';
export type CashMovementType =
  | 'expense'
  | 'income'
  | 'staff_advance'
  | 'commission_payout'
  | 'gift_card_sale'
  | 'gift_card_refund'
  | 'stock_purchase';
export type PaymentKind = 'cash' | 'transfer' | 'debit' | 'credit' | 'wallet' | 'other';
export type DiscountType = 'percent' | 'amount';
export type GiftCardKind = 'amount' | 'services';
export type GiftCardState = 'active' | 'partial' | 'used' | 'expired' | 'voided';
export type ClientStatus = 'has_appointment' | 'no_visits' | 'on_track' | 'to_recover' | 'lost';
export type StreakMarkKind = 'on_time' | 'late' | 'very_late' | 'first' | 'no_show';
export type StreakNextKind = 'appointment' | 'early' | 'due' | 'late';
export type OptOutSource = 'replied_stop' | 'in_person' | 'public_link' | 'other';
export type CancelledVia = 'staff' | 'client_link';
export type CancelReasonCode =
  | 'client_notified'
  | 'could_not_contact'
  | 'data_entry_error'
  | 'business_cancelled'
  | 'other';
export type AppointmentEventKind =
  | 'created'
  | 'updated'
  | 'rescheduled'
  | 'status_changed'
  | 'overbook_confirmed'
  | 'seen'
  | 'reminder_opened'
  | 'reminder_sent'
  | 'reminder_unmarked'
  | 'sale_linked'
  | 'sale_voided';
/** Parámetros "rol mínimo" (orden manager < owner; el profesional nunca). */
export type MinRole = 'owner' | 'manager';
export type AcceptExpiredRole = 'owner' | 'manager' | 'nobody';
/** Valor de `location_branding.font` (CHECK de DATA_MODEL; DATA_MODEL §7.6 traduce las claves de GEN-50). */
export type BrandFont = 'modern' | 'classic' | 'elegant' | 'editorial' | 'rounded';

/** Rango horario en minutos desde las 00:00 locales (0..1440). */
export interface TimeRange {
  open: number;
  close: number;
}

/** Estados que ocupan el horario del profesional (AGE-03). */
export const BLOCKING_STATUSES: readonly AppointmentStatus[] = Object.freeze([
  'booked',
  'confirmed',
  'completed',
] as const);

/** Turno abierto (§0.2). */
export const OPEN_STATUSES: readonly AppointmentStatus[] = Object.freeze(['booked', 'confirmed'] as const);

export const ROLES: readonly Role[] = Object.freeze(['owner', 'manager', 'staff'] as const);
export const VERTICAL_KEYS: readonly Vertical[] = Object.freeze([
  'barberia',
  'peluqueria',
  'estetica',
  'unas',
  'salud',
  'spa',
  'otro',
] as const);
export const MODULE_KEYS: readonly ModuleKey[] = Object.freeze([
  'sales',
  'stock',
  'commissions',
  'giftcards',
  'finance',
  'dashboard',
] as const);

/** Reloj del servidor en la zona de un local (GEN-10). */
export interface Clock {
  now: Instant;
  today: ISODate;
  nowMin: number;
  timezone: string;
}

export function isBlockingStatus(s: AppointmentStatus): boolean {
  return BLOCKING_STATUSES.includes(s);
}

export function isOpenStatus(s: AppointmentStatus): boolean {
  return OPEN_STATUSES.includes(s);
}
